import { ComponentTypes, WidgetComponent } from 'api/types';
import { PartialDeep } from 'type-fest';
import { invisibleComponentTypes } from './invisibleComponentTypes';

const componentTypesThatHaveMessageBubbles: ComponentTypes[] = [
  'buttons',
  'restart_conversation',
  'text_message',
  'user_message',
  'visible_only_to_admins',
  'hyperlink_redirect',
  'form',
  'dynamic_card',
  'article_suggestion',
  'attachment_analysis_upload',
  'attachment_upload',
  'zendesk_attachment_upload',
] as const;

export type MessageBubblePosition =
  | 'lone'
  | 'first-in-a-batch'
  | 'middle'
  | 'last-in-a-batch';

function hasMessageBubble(
  widgetComponent: PartialDeep<WidgetComponent> | undefined,
) {
  return (
    widgetComponent?.component_type &&
    componentTypesThatHaveMessageBubbles.includes(
      widgetComponent.component_type,
    ) &&
    !(
      widgetComponent?.component_type === 'text_message' &&
      widgetComponent.isStreamingComponent &&
      widgetComponent.component_fields &&
      'should_render_as_markdown' in widgetComponent.component_fields &&
      widgetComponent.component_fields.should_render_as_markdown &&
      'knowledge_articles' in widgetComponent.component_fields &&
      widgetComponent.component_fields.knowledge_articles
    )
  );
}

export default function getMessageBubblePositions(
  widgetComponents: PartialDeep<WidgetComponent>[],
) {
  return Object.fromEntries<{
    bot?: MessageBubblePosition;
    user?: MessageBubblePosition;
  } | null>(
    widgetComponents
      .map((widgetComponent, index) => ({ ...widgetComponent, index }))
      .filter(
        widgetComponent =>
          widgetComponent.component_type &&
          !invisibleComponentTypes.includes(widgetComponent.component_type),
      )
      .map((widgetComponent, index, visibleWidgetComponents) => {
        const nextVisibleWidgetComponent = visibleWidgetComponents[index + 1];
        const previousVisibleWidgetComponent =
          visibleWidgetComponents[index - 1];

        if (widgetComponent.index === 0) {
          return [
            widgetComponent.index,
            {
              bot: 'lone',
              ...(widgetComponent.isSent ? { user: 'lone' } : {}),
            },
          ];
        }

        if (!hasMessageBubble(widgetComponent)) {
          return [widgetComponent.index, null];
        }

        if (widgetComponent.component_type === 'user_message') {
          if (
            visibleWidgetComponents[index - 1]?.component_type !==
            'user_message'
          ) {
            return [
              widgetComponent.index,
              {
                user:
                  visibleWidgetComponents[index + 1]?.component_type ===
                  'user_message'
                    ? 'first-in-a-batch'
                    : 'lone',
              },
            ];
          }

          if (
            visibleWidgetComponents[index - 1]?.component_type ===
            'user_message'
          ) {
            return [
              widgetComponent.index,
              {
                user:
                  visibleWidgetComponents[index + 1]?.component_type !==
                  'user_message'
                    ? 'last-in-a-batch'
                    : 'middle',
              },
            ];
          }

          return [widgetComponent.index, { user: 'last-in-a-batch' }];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.component_type === 'text_message' &&
          widgetComponent.isSent &&
          previousVisibleWidgetComponent &&
          'isInAgentConvo' in previousVisibleWidgetComponent &&
          previousVisibleWidgetComponent.isInAgentConvo &&
          hasMessageBubble(previousVisibleWidgetComponent) &&
          previousVisibleWidgetComponent?.isSent &&
          previousVisibleWidgetComponent.component_type === 'text_message' &&
          nextVisibleWidgetComponent &&
          'isInAgentConvo' in nextVisibleWidgetComponent &&
          nextVisibleWidgetComponent.isInAgentConvo &&
          hasMessageBubble(nextVisibleWidgetComponent) &&
          nextVisibleWidgetComponent?.isSent &&
          nextVisibleWidgetComponent.component_type === 'text_message'
        ) {
          return [
            widgetComponent.index,
            {
              bot: 'middle',
              ...(widgetComponent.isSent ? { user: 'lone' } : {}),
            },
          ];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.component_type === 'buttons' &&
          hasMessageBubble(previousVisibleWidgetComponent) &&
          visibleWidgetComponents[index + 1]
        ) {
          return [
            widgetComponent.index,
            {
              bot: widgetComponent.isSent ? 'last-in-a-batch' : 'middle',
              ...(widgetComponent.isSent ? { user: 'lone' } : {}),
            },
          ];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          !visibleWidgetComponents[index - 1]?.isSent &&
          hasMessageBubble(previousVisibleWidgetComponent) &&
          visibleWidgetComponents[index + 1]
        ) {
          return [widgetComponent.index, { bot: 'middle' }];
        }

        if (
          (!widgetComponent.isSent ||
            widgetComponent.component_type === 'hyperlink_redirect') &&
          !visibleWidgetComponents[index - 1]?.isSent &&
          visibleWidgetComponents[index + 1] &&
          hasMessageBubble(previousVisibleWidgetComponent) &&
          hasMessageBubble(nextVisibleWidgetComponent)
        ) {
          return [widgetComponent.index, { bot: 'middle' }];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.isSent &&
          widgetComponent.component_type === 'text_message' && // ????????
          previousVisibleWidgetComponent?.component_type === 'user_message' &&
          nextVisibleWidgetComponent?.component_type === 'user_message'
        ) {
          return [widgetComponent.index, { bot: 'lone' }];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.isSent &&
          widgetComponent.component_type === 'text_message' && // ????????
          (visibleWidgetComponents[index - 1]?.isSent ||
            !hasMessageBubble(previousVisibleWidgetComponent)) &&
          visibleWidgetComponents[index - 1]?.component_type !==
            'text_message' &&
          hasMessageBubble(nextVisibleWidgetComponent)
        ) {
          return [
            widgetComponent.index,
            { bot: 'first-in-a-batch', user: 'lone' },
          ];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.isSent &&
          !hasMessageBubble(previousVisibleWidgetComponent) &&
          !hasMessageBubble(nextVisibleWidgetComponent)
        ) {
          return [
            widgetComponent.index,
            {
              bot: 'lone',
              user: 'lone',
            },
          ];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.isSent
        ) {
          return [
            widgetComponent.index,
            {
              bot:
                previousVisibleWidgetComponent?.component_type ===
                'user_message'
                  ? 'lone'
                  : 'last-in-a-batch',
              user: 'lone',
            },
          ];
        }

        if (
          widgetComponent.isSent &&
          !visibleWidgetComponents[index - 1]?.isSent
        ) {
          return [
            widgetComponent.index,
            { bot: 'last-in-a-batch', user: 'lone' },
          ];
        }

        if (
          (!visibleWidgetComponents[index - 1]?.isSent ||
            visibleWidgetComponents[index - 1]?.component_type ===
              'hyperlink_redirect') &&
          hasMessageBubble(previousVisibleWidgetComponent)
        ) {
          return [
            widgetComponent.index,
            {
              bot: 'last-in-a-batch',
              ...(widgetComponent.isSent ? { user: 'lone' } : {}),
            },
          ];
        }

        if (
          !widgetComponent.isSent &&
          visibleWidgetComponents[index + 1] &&
          hasMessageBubble(nextVisibleWidgetComponent)
        ) {
          return [widgetComponent.index, { bot: 'first-in-a-batch' }];
        }

        if (
          'isInAgentConvo' in widgetComponent &&
          widgetComponent.isInAgentConvo &&
          widgetComponent.component_type === 'buttons' &&
          hasMessageBubble(previousVisibleWidgetComponent) &&
          nextVisibleWidgetComponent === undefined
        ) {
          return [
            widgetComponent.index,
            { bot: 'last-in-a-batch', user: 'lone' },
          ];
        }

        return [
          widgetComponent.index,
          { bot: 'lone', ...(widgetComponent.isSent ? { user: 'lone' } : {}) },
        ];
      }),
  );
}
